'use client';

import { createContext, useEffect, useState, type ReactNode } from 'react';

export type ColorMode = 'light' | 'dark' | 'auto';
export type ResolvedColorMode = 'light' | 'dark';

export interface ColorModeContextValue {
  mode: ColorMode;
  setMode: (mode: ColorMode) => void;
  resolvedMode: ResolvedColorMode;
}

export const ColorModeContext = createContext<ColorModeContextValue | null>(null);

const STORAGE_KEY = 'heyroot-color-mode';

function resolve(mode: ColorMode): ResolvedColorMode {
  if (mode !== 'auto') return mode;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readInitial(initialMode: ColorMode): ColorMode {
  if (typeof window === 'undefined') return initialMode;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored;
  } catch {
    // Keep the supplied preference when browser storage is unavailable.
  }
  return initialMode;
}

export interface ColorModeProviderProps {
  initialMode?: ColorMode;
  children: ReactNode;
}

export function ColorModeProvider({ initialMode = 'auto', children }: ColorModeProviderProps) {
  const [mode, setModeInternal] = useState<ColorMode>(() => readInitial(initialMode));
  const [, forceTick] = useState(0);
  const resolvedMode = resolve(mode);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolvedMode === 'dark');
  }, [resolvedMode]);

  useEffect(() => {
    if (mode !== 'auto') return;
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => forceTick((t) => t + 1);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [mode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      if (e.newValue === 'light' || e.newValue === 'dark' || e.newValue === 'auto') {
        setModeInternal(e.newValue);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  function setMode(next: ColorMode) {
    setModeInternal(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      console.warn('[heyroot/ui] failed to persist preference to localStorage:', err);
    }
  }

  return (
    <ColorModeContext.Provider value={{ mode, setMode, resolvedMode }}>
      {children}
    </ColorModeContext.Provider>
  );
}
